import { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import { DocumentDownload, Printer } from "iconsax-react";
import DBModal from "../../../../component/global/DBModal";
import DBButton from "../../../../component/global/DBButton";
import DBSpinner from "../../../../component/global/DBSpinner";
import { ToastContext } from "../../../../component/global/ToastContext";
import { collectTreeIds } from "../../../../component/global/DBSearchableTree/treeUtils";
import {
    SETTING_ITEMS_LIST,
    itemPrintPosterUrl,
    itemQrCodeUrl,
} from "../../../../helperData/ApiAddress";
import PrintPosterPreview from "../feedback-page-tab/print-poster/PrintPosterPreview";
import { emptyPoster } from "../feedback-page-tab/print-poster/constants";
import {
    downloadAllPosterPngs,
    downloadAllQrPngs,
    downloadPosterPng,
    printPosterNode,
    printPosterNodes,
    safePosterFilename,
} from "../feedback-page-tab/print-poster/printPosterExport";

function authHeaders() {
    const auth = sessionStorage.getItem("auth");
    return {
        Authorization: auth ? `Bearer ${auth}` : undefined,
        "Accept-Language": "fa",
    };
}

export default function PrintPosterDownloadModal({
    show,
    onHide,
    item,
    bulk = false,
    itemType,
    searchName,
}) {
    const toast = useContext(ToastContext);
    const previewRef = useRef(null);
    const objectUrlsRef = useRef([]);
    const abortRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState("");
    const [progress, setProgress] = useState(null);
    const [poster, setPoster] = useState(emptyPoster());
    const [company, setCompany] = useState(null);
    const [qrSrc, setQrSrc] = useState("");
    const [previewItem, setPreviewItem] = useState(null);
    const [bulkItems, setBulkItems] = useState([]);

    const revokeObjectUrls = () => {
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
    };

    const trackObjectUrl = (url) => {
        objectUrlsRef.current.push(url);
        return url;
    };

    const loadQrSrc = async (target) => {
        const qr = await axios.get(itemQrCodeUrl(target.id), {
            headers: authHeaders(),
            responseType: "blob",
        });
        return URL.createObjectURL(qr.data);
    };

    const loadQrBlob = async (target) => {
        const qr = await axios.get(itemQrCodeUrl(target.id), {
            headers: authHeaders(),
            responseType: "blob",
        });
        return qr.data;
    };

    useEffect(() => {
        abortRef.current = !show;
        if (!show) {
            setBusy("");
            setProgress(null);
            setBulkItems([]);
            setPreviewItem(null);
            setQrSrc("");
            revokeObjectUrls();
        }
    }, [show]);

    useEffect(() => {
        if (!show) return undefined;
        if (!bulk && !item?.id) return undefined;
        let cancelled = false;
        const headers = authHeaders();
        setLoading(true);
        setProgress(null);

        const load = async () => {
            try {
                let targets = item?.id ? [item] : [];
                if (bulk) {
                    const list = await axios.get(SETTING_ITEMS_LIST, {
                        headers,
                        params: {
                            item_type_in: collectTreeIds([itemType]).join(","),
                            name: searchName || undefined,
                        },
                    });
                    targets = Array.isArray(list.data) ? list.data : [];
                    if (!targets.length) {
                        if (!cancelled) {
                            toast(t("print.poster.download.all.empty"), "danger");
                            onHide();
                        }
                        return;
                    }
                }
                const first = targets[0];
                const [meta, qr] = await Promise.all([
                    axios.get(itemPrintPosterUrl(first.id), { headers }),
                    axios.get(itemQrCodeUrl(first.id), {
                        headers,
                        responseType: "blob",
                    }),
                ]);
                const objectUrl = URL.createObjectURL(qr.data);
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                trackObjectUrl(objectUrl);
                setBulkItems(targets);
                setPreviewItem(first);
                setPoster(meta.data.poster || emptyPoster());
                setCompany(meta.data.company || null);
                setQrSrc(objectUrl);
            } catch (_) {
                if (!cancelled) {
                    toast(t("print.poster.download.error"), "danger");
                    onHide();
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [show, bulk, item?.id, itemType?.id, searchName]);

    const runBusy = async (kind, action) => {
        setBusy(kind);
        abortRef.current = false;
        try {
            await action();
        } catch (_) {
            toast(
                kind === "qr"
                    ? t("print.poster.download.error")
                    : t("print.poster.download.png.error"),
                "danger"
            );
        } finally {
            setBusy("");
            setProgress(null);
        }
    };

    const zipBaseName = safePosterFilename(itemType?.name || "items");
    const isBulk = bulk && bulkItems.length > 1;
    const signal = {
        get aborted() {
            return abortRef.current;
        },
    };

    const handlePrint = () =>
        runBusy("print", async () => {
            if (isBulk) {
                await printPosterNodes({
                    baseNode: previewRef.current,
                    poster,
                    items: bulkItems,
                    loadQrSrc,
                    onProgress: (current, total) =>
                        setProgress({ current, total }),
                    signal,
                });
                return;
            }
            await printPosterNode(previewRef.current);
        });

    const handlePng = () =>
        runBusy("png", async () => {
            if (bulk) {
                await downloadAllPosterPngs({
                    baseNode: previewRef.current,
                    poster,
                    items: bulkItems,
                    loadQrSrc,
                    zipName: `${zipBaseName}-print-posters.zip`,
                    onProgress: (current, total) =>
                        setProgress({ current, total }),
                    signal,
                });
                return;
            }
            await downloadPosterPng({
                node: previewRef.current,
                filename: `${previewItem?.name || "qr"}-poster.png`,
            });
        });

    const handleQrOnly = () => {
        if (bulk) {
            runBusy("qr", async () => {
                await downloadAllQrPngs({
                    items: bulkItems,
                    loadQrBlob,
                    zipName: `${zipBaseName}-qr-codes.zip`,
                    onProgress: (current, total) =>
                        setProgress({ current, total }),
                    signal,
                });
            });
            return;
        }
        window.open(itemQrCodeUrl(previewItem?.id || item?.id));
    };

    const handleHide = () => {
        abortRef.current = true;
        onHide();
    };

    return (
        <DBModal
            show={show}
            onHide={handleHide}
            minWidth={720}
            className="print-poster-download-modal"
            ariaLabelledby="print-poster-download-title"
        >
            <DBModal.Header
                closeBtn={handleHide}
                title={
                    <span id="print-poster-download-title">
                        {t("print.poster.download.title")}
                    </span>
                }
            />
            <DBModal.Content>
                {loading ? (
                    <DBSpinner tip={t("loading")} />
                ) : (
                    <>
                        <p className="print-poster-download-lead">
                            {bulk
                                ? t("print.poster.download.all.lead", {
                                      count: bulkItems.length,
                                  })
                                : t("print.poster.download.lead", {
                                      name: previewItem?.name || item?.name,
                                  })}
                        </p>
                        {progress ? (
                            <p className="print-poster-download-progress">
                                {t("print.poster.download.all.progress", {
                                    current: progress.current,
                                    total: progress.total,
                                })}
                            </p>
                        ) : null}
                        <div className="print-poster-download-preview">
                            <PrintPosterPreview
                                poster={poster}
                                company={company}
                                itemName={previewItem?.name || item?.name}
                                qrSrc={qrSrc}
                                previewRef={previewRef}
                            />
                        </div>
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    type="secondary"
                    caption={t("cancel")}
                    onClick={handleHide}
                />
                <DBButton
                    type="secondary"
                    caption={
                        bulk
                            ? t("print.poster.download.qr.only.all")
                            : t("print.poster.download.qr.only")
                    }
                    onClick={handleQrOnly}
                    loading={busy === "qr"}
                    disabled={loading || Boolean(busy)}
                />
                <DBButton
                    type="secondary"
                    caption={
                        <>
                            <DocumentDownload size={18} aria-hidden="true" />
                            <span>
                                {bulk
                                    ? t("print.poster.download.png.all")
                                    : t("print.poster.download.png")}
                            </span>
                        </>
                    }
                    loading={busy === "png"}
                    disabled={loading || Boolean(busy)}
                    onClick={handlePng}
                />
                <DBButton
                    caption={
                        <>
                            <Printer size={18} aria-hidden="true" />
                            <span>{t("print.poster.download.print")}</span>
                        </>
                    }
                    loading={busy === "print"}
                    disabled={loading || Boolean(busy)}
                    onClick={handlePrint}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
